import { z } from 'zod'

import type { RequestService } from '../request'
import { nanoidSchema } from '@scalar-org/entities/utility'
import { accessGroupSchema } from '@scalar-org/entities/access-group'

/**
 * API for interacting with team Access Groups
 *
 * This is a wrapper around the core/v1/access-groups endpoints in the core service
 */
export function accessGroupsApiFactory(requestService: RequestService) {
  async function get(slug: string) {
    return await requestService.request({
      url: `core/v1/access-groups/${slug}`,
      method: 'get',
      schema: accessGroupSchema.pick({
        uid: true,
        name: true,
        slug: true,
        allowedDomains: true,
        allowedEmails: true,
      }),
    })
  }

  /** Create a new access group */
  async function create(data: {
    name?: string
    slug?: string
    allowedEmails?: string[]
    allowedDomains?: string[]
  }) {
    return await requestService.request({
      url: 'core/v1/access-groups',
      method: 'post',
      data,
      schema: nanoidSchema,
    })
  }

  /** Delete an access group */
  async function del(uid: string) {
    return await requestService.request({
      url: `core/v1/access-groups/${uid}`,
      method: 'delete',
      schema: z.null(),
    })
  }

  /** Update an access group */
  async function update(
    uid: string,
    data: {
      name?: string
      slug?: string
    },
  ) {
    return await requestService.request({
      url: `core/v1/access-groups/${uid}`,
      method: 'PATCH',
      data,
      schema: z.null(),
    })
  }

  /** Add email in access group */
  async function addEmail(uid: string, email: string) {
    return await requestService.request({
      url: `core/v1/access-groups/${uid}/emails`,
      method: 'post',
      data: { email },
      schema: z.null(),
    })
  }

  /** Add domain in access group */
  async function addDomain(uid: string, domain: string) {
    return await requestService.request({
      url: `core/v1/access-groups/${uid}/domains`,
      method: 'post',
      data: { domain },
      schema: z.null(),
    })
  }

  /** Remove email from access group */
  async function removeEmail(uid: string, email: string) {
    return await requestService.request({
      url: `core/v1/access-groups/${uid}/emails`,
      method: 'delete',
      data: { email },
      schema: z.null(),
    })
  }

  /** Remove domain from access group */
  async function removeDomain(uid: string, domain: string) {
    return await requestService.request({
      url: `core/v1/access-groups/${uid}/domains`,
      method: 'delete',
      data: { domain },
      schema: z.null(),
    })
  }

  return {
    get,
    create,
    update,
    delete: del,
    addEmail,
    addDomain,
    removeEmail,
    removeDomain,
  }
}
